import { InputAdornment } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useField } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  STextField,
  StyledListBox,
  StyledOptions,
} from 'app/components/RegionSelect/styles';
import { CountriesKeys, supportedCountries } from 'service/countrisList';

const preferredOrder = ['US', 'DE', 'GB', 'EE', 'FR'].reverse();

const options = Object.values(supportedCountries).sort((A, B) => {
  if (
    preferredOrder.indexOf(A.key) >= 0 ||
    preferredOrder.indexOf(B.key) >= 0
  ) {
    return preferredOrder.indexOf(B.key) - preferredOrder.indexOf(A.key);
  }
  if (A.native === B.native) {
    return 0;
  }
  return A.native < B.native ? -1 : 1;
});

type Props = {
  children?: never;
  path: string;
  label: string;
};

const useTouched = ({
  setTouched,
  value,
}: {
  value: string | null;
  setTouched: (value: boolean, shouldValidate?: boolean | undefined) => void;
}) => {
  useEffect(() => {
    if (value !== null) {
      setTouched(true, true);
    }
    // trigger only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
};

const RegionSelect: React.FC<Props> = ({ path, label }) => {
  const { t } = useTranslation();
  const [{ value }, { error, touched }, { setValue, setTouched }] = useField<
    string | null
  >(`${path}.${label}`);

  useTouched({ setTouched, value });

  return (
    <Autocomplete
      className="form-field__max-width"
      value={value ? supportedCountries[value as CountriesKeys] : null}
      options={options}
      onChange={(e, newValue) => {
        setValue(newValue?.key ?? null);
        setTouched(true, true);
      }}
      autoHighlight
      getOptionLabel={(option) => option?.native ?? ''}
      renderOption={(option) => (
        <StyledOptions
          option={`url(https://assets.erply.com/global/icons/flags/svg/${option.key}.svg)`}
        >
          {option.native}
        </StyledOptions>
      )}
      ListboxComponent={StyledListBox}
      renderInput={({ inputProps, InputProps, ...params }) => (
        <STextField
          {...params}
          variant="outlined"
          label={t('registration.component.countrySelect')}
          error={error !== undefined && touched}
          helperText={error !== undefined && touched ? error : undefined}
          InputProps={{
            ...InputProps,
            startAdornment: value && (
              <InputAdornment position="start" style={{ marginLeft: '0.5rem' }}>
                <StyledOptions
                  option={`url(https://assets.erply.com/global/icons/flags/svg/${value}.svg)`}
                />
              </InputAdornment>
            ),
          }}
          inputProps={{
            ...inputProps,
            autoComplete: 'new-password',
          }}
        />
      )}
    />
  );
};

export default RegionSelect;
